export type StoreEquals<T> = (previous: T, next: T) => boolean;

export interface StoreSubjectOptions<T> {
  equals?: StoreEquals<T>;
}

let batchDepth = 0;
const batchQueue: Array<() => void> = [];

/**
 * Runs updates without notifying subscribers until the outer batch completes.
 * Nested batches are supported.
 */
export function batch(update: () => void): void {
  batchDepth += 1;
  try {
    update();
  } finally {
    batchDepth -= 1;
    if (batchDepth === 0) {
      const queue = batchQueue.splice(0);
      for (const notify of queue) {
        notify();
      }
    }
  }
}

export function storeBatchDepth(): number {
  return batchDepth;
}

export class StoreSubject<T> {
  private value: T;
  private readonly equals: StoreEquals<T>;
  private subscribers: Array<(value: T) => void> = [];
  private batchNotifyQueued = false;

  constructor(initial: T, options: StoreSubjectOptions<T> = {}) {
    this.value = initial;
    this.equals = options.equals ?? Object.is;
  }

  getValue(): T {
    return this.value;
  }

  next(value: T): void {
    if (this.equals(this.value, value)) {
      return;
    }

    this.value = value;

    if (batchDepth > 0) {
      if (!this.batchNotifyQueued) {
        this.batchNotifyQueued = true;
        batchQueue.push(() => this.flush());
      }
      return;
    }

    this.flush();
  }

  subscribe(listener: (value: T) => void): () => void {
    this.subscribers.push(listener);
    listener(this.value);
    return () => {
      this.subscribers = this.subscribers.filter(subscriber => subscriber !== listener);
    };
  }

  private flush(): void {
    this.batchNotifyQueued = false;
    const listeners = this.subscribers;
    for (let index = 0; index < listeners.length; index += 1) {
      listeners[index]!(this.value);
    }
  }
}
